<script setup lang="ts">
import type {
  EventResponse,
  PaymentMethodResponse,
  RegistrationTypeResponse,
} from '@@/types/api';
import { formatCurrency } from '~/composables/format-currency';
import { useEventPricing } from '~/composables/api/use-event-pricing';

const props = defineProps<{
  modelValue: boolean;
  event: EventResponse;
  isSubmitting: boolean;
  paymentMethods: PaymentMethodResponse[];
  isLoadingMethods: boolean;
  existingTypeId?: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  submit: [
    paymentMethodId: string,
    registrationTypeId: string,
    partySize: number,
  ];
  'card-added': [];
}>();

const termsAccepted = ref(false);
const selectedTypeId = ref('');
const partySize = ref(1);

const registrationTypes = computed(() => props.event.registration_types ?? []);
const isMultiType = computed(() => registrationTypes.value.length > 1);
const isSupplemental = computed(() => !!props.existingTypeId);

const selectedType = computed<RegistrationTypeResponse | undefined>(() =>
  registrationTypes.value.find((rt) => rt.id === selectedTypeId.value),
);

const pricing = useEventPricing(
  () => props.event.id,
  () => selectedTypeId.value || null,
  partySize,
  'purchase',
  {
    isSupplemental,
    enabled: () => props.modelValue,
  },
);

const submitLabel = computed(() => {
  const total = pricing.breakdown.value?.total;
  return total != null
    ? `Purchase \u2014 ${formatCurrency(total)}`
    : 'Purchase';
});

const maxPartySize = computed(() => selectedType.value?.max_party_size ?? null);

const partySizeRules = computed(() => {
  const max = maxPartySize.value;
  return [
    (v: unknown) =>
      (Number.isInteger(Number(v)) && Number(v) >= 1) ||
      'Must be a whole number of at least 1',
    (v: unknown) =>
      max == null || Number(v) <= max || `Maximum ${max} per registration`,
  ];
});

const partySizeValid = computed(() => {
  if (!Number.isInteger(partySize.value) || partySize.value < 1) return false;
  if (maxPartySize.value != null && partySize.value > maxPartySize.value)
    return false;
  return true;
});

const spotsRemaining = computed(() => {
  if (selectedType.value) {
    return selectedType.value.registrations_remaining;
  }
  if (
    props.event.max_tickets == null ||
    props.event.tickets_remaining == null
  ) {
    return null;
  }
  return props.event.tickets_remaining;
});

const canSubmit = computed(
  () =>
    termsAccepted.value &&
    !props.isSubmitting &&
    selectedTypeId.value !== '' &&
    partySizeValid.value &&
    !pricing.isLoading.value &&
    !pricing.isStale.value &&
    pricing.breakdown.value !== null &&
    pricing.error.value === null,
);

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      if (props.existingTypeId) {
        // Supplemental purchase: auto-select existing type
        selectedTypeId.value = props.existingTypeId;
      } else {
        // Auto-select first available type
        const available = registrationTypes.value.filter(
          (rt) => !rt.is_sold_out,
        );
        selectedTypeId.value = available[0]?.id ?? '';
      }
      partySize.value = 1;
    } else {
      termsAccepted.value = false;
      selectedTypeId.value = '';
      partySize.value = 1;
    }
  },
);

function close() {
  emit('update:modelValue', false);
}

function handleSubmit(paymentMethodId: string) {
  emit('submit', paymentMethodId, selectedTypeId.value, partySize.value);
}
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="520"
    :persistent="isSubmitting"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title class="text-h6">Purchase ticket</v-card-title>
      <v-card-text>
        <!-- Registration type selector (multi-type only, not supplemental) -->
        <template v-if="isMultiType && !isSupplemental">
          <div class="text-subtitle-2 mb-2">Select registration type</div>
          <v-radio-group v-model="selectedTypeId" class="mb-4" hide-details>
            <v-radio
              v-for="rt in registrationTypes"
              :key="rt.id"
              :value="rt.id"
              :disabled="rt.is_sold_out"
              class="mb-1"
            >
              <template #label>
                <div class="d-flex align-center ga-2 w-100">
                  <div>
                    <span class="text-body-2 font-weight-medium">
                      {{ rt.name }}
                    </span>
                    <span class="text-body-2 text-medium-emphasis ml-2">
                      {{ formatCurrency(rt.pricing.purchase.total) }}
                    </span>
                  </div>
                  <v-chip
                    v-if="rt.is_sold_out"
                    color="error"
                    size="x-small"
                    label
                  >
                    Sold out
                  </v-chip>
                  <span
                    v-else-if="rt.registrations_remaining != null"
                    class="text-caption text-medium-emphasis"
                  >
                    {{ rt.registrations_remaining }} left
                  </span>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
          <v-divider class="mb-4" />
        </template>

        <!-- Supplemental purchase info -->
        <v-alert
          v-if="isSupplemental && selectedType"
          type="info"
          density="compact"
          variant="tonal"
          class="mb-4"
        >
          Purchasing additional tickets for {{ selectedType.name }}.
        </v-alert>

        <!-- Pricing breakdown -->
        <EventsPricingBreakdown
          :breakdown="pricing.breakdown.value"
          :party-size="partySize"
          :is-initial-loading="pricing.isInitialLoading.value"
          :is-refetching="pricing.isRefetching.value"
          :error-message="pricing.error.value?.message ?? null"
        />

        <!-- Party size -->
        <v-text-field
          v-model.number="partySize"
          label="Number of attendees"
          type="number"
          min="1"
          :max="maxPartySize ?? undefined"
          :rules="partySizeRules"
          :hint="
            maxPartySize
              ? `Maximum ${maxPartySize} per registration`
              : undefined
          "
          persistent-hint
          density="compact"
          class="mb-4"
          style="max-width: 240px"
        />

        <!-- Capacity warning -->
        <v-chip
          v-if="spotsRemaining !== null"
          color="warning"
          size="small"
          label
          class="mb-4"
        >
          {{ spotsRemaining }}
          {{ spotsRemaining === 1 ? 'spot' : 'spots' }} remaining
        </v-chip>

        <!-- Terms -->
        <v-checkbox
          v-model="termsAccepted"
          density="compact"
          hide-details
          class="mb-4"
          data-testid="terms-checkbox"
        >
          <template #label>
            <span class="text-body-2">
              I understand my card will be charged immediately. This event is
              organized by the event host, not by Arvee. By participating, you
              agree to follow the organizer's rules and Arvee's community
              guidelines.
            </span>
          </template>
        </v-checkbox>

        <!-- Payment method selection -->
        <StripePaymentMethodSelector
          :payment-methods="paymentMethods"
          :is-loading-methods="isLoadingMethods"
          :submit-label="submitLabel"
          :disabled="!canSubmit"
          @submit="handleSubmit"
          @card-added="emit('card-added')"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" :disabled="isSubmitting" @click="close">
          Cancel
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
